'use client';

import React, { memo } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { cn } from '@/shared/lib/cn';
import { formatEstimateMoney } from '@/shared/lib/utils';
import { EstimateDocumentView } from '@/features/design/estimate';
import type { EstimateStatus } from '@/features/design/estimate';
import type { HistoryItem, JobStatus } from '../model/history.types';

// Unified job vocabulary → raw estimate status, so the document overlay reuses
// the same `common.estimate.status.*` words the Estimate tab already shows.
const JOB_TO_ESTIMATE_STATUS: Record<JobStatus, EstimateStatus> = {
  pending: 'pending',
  processing: 'generating',
  completed: 'ready',
  failed: 'failed',
};

interface EstimateHistoryCardProps {
  item: HistoryItem;
  /** Dashboard carousels pass `false` to show the document only (parity with AlbumCard). */
  showLabel?: boolean;
}

/**
 * Estimate card for the history grid — sibling of AlbumCard. The visual is the
 * stylized estimate "document" (real materials/labor/total figures, no photo);
 * tapping opens the owning concept's Estimate tab (parity with how an album
 * card opens `?tab=album`).
 */
export const EstimateHistoryCard = memo(function EstimateHistoryCard({
  item,
  showLabel = true,
}: EstimateHistoryCardProps) {
  const t = useTranslations('common.estimate');
  const tConcept = useTranslations('concept');
  const router = useRouter();
  const attrs = item.attributes;

  const projectSequenceNumber = attrs.project_sequence_number || 0;
  const conceptTitle = attrs.name || '';
  const displayTitle =
    conceptTitle ||
    tConcept('conceptNumber', { number: projectSequenceNumber });
  const projectId = item.relationships.project.data.id;
  const conceptId = item.conceptId;

  const status: JobStatus = attrs.estimate_status ?? 'completed';
  // Estimates no longer carry a user-set title — the card label is the
  // localized document name.
  const title = t('card.untitled');
  const grandTotal = attrs.estimate_grand_total;
  const currency = attrs.estimate_currency;

  const handleClick = () => {
    if (conceptId && projectId) {
      router.push(`/project/${projectId}/concept/${conceptId}?tab=estimate`);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'block group',
        conceptId ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
      )}
    >
      <div className="aspect-[4/3] relative transition-all hover:scale-[1.01]">
        <EstimateDocumentView
          materialsTotal={attrs.estimate_materials_total ?? 0}
          laborTotal={attrs.estimate_labor_total ?? 0}
          grandTotal={grandTotal ?? 0}
          currency={currency}
          conceptNumber={projectSequenceNumber}
          createdAt={attrs.created_at}
          status={JOB_TO_ESTIMATE_STATUS[status]}
        />
      </div>

      {showLabel && (
        <div className="px-2 pt-2 pb-1">
          <p className="text-list-item-value truncate">{title}</p>
          <p className="text-card-meta text-muted-foreground truncate">
            {status === 'completed' && grandTotal != null && currency
              ? `${formatEstimateMoney(grandTotal, currency)} · ${displayTitle}`
              : displayTitle}
          </p>
        </div>
      )}
    </div>
  );
});
